import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Plus, FileText, Download, Trash2, Copy, FileType } from "lucide-react";
import { toast } from "sonner";

export default function ReqLibrary() {
  const navigate = useNavigate();
  const [libraryReqs, setLibraryReqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLibraryReqs();
  }, []);

  const fetchLibraryReqs = async () => {
    try {
      const response = await apiClient.get("/requisitions/library");
      setLibraryReqs(response.data || []);
    } catch (error) {
      console.error("Failed to fetch library requisitions:", error);
      // Only show error if it's not a 404 (empty library)
      if (error.response?.status !== 404) {
        toast.error("Failed to load library");
      }
      setLibraryReqs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this requisition from the library?")) {
      return;
    }

    try {
      await apiClient.delete(`/requisitions/library/${id}`);
      toast.success("Requisition deleted from library");
      fetchLibraryReqs();
    } catch (error) {
      toast.error("Failed to delete requisition");
    }
  };

  const handleCopyToClipboard = async (req) => {
    try {
      const response = await apiClient.get(`/requisitions/library/${req.id}/export/json`);
      await navigator.clipboard.writeText(response.data.content);
      toast.success("Copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleExportPDF = async (req) => {
    try {
      const response = await apiClient.get(`/requisitions/library/${req.id}/export/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = (req.name || req.title || 'requisition').replace(/\s+/g, '_');
      link.setAttribute('download', `${filename}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    }
  };

  const handleExportWord = async (req) => {
    try {
      const response = await apiClient.get(`/requisitions/library/${req.id}/export/word`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = (req.name || req.title || 'requisition').replace(/\s+/g, '_');
      link.setAttribute('download', `${filename}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Word document downloaded!");
    } catch (error) {
      console.error("Word export error:", error);
      toast.error("Failed to export Word document");
    }
  };

  const handleExportText = async (req) => {
    try {
      const response = await apiClient.get(`/requisitions/library/${req.id}/export/text`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = (req.name || req.title || 'requisition').replace(/\s+/g, '_');
      link.setAttribute('download', `${filename}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Text file downloaded!");
    } catch (error) {
      console.error("Text export error:", error);
      toast.error("Failed to export text file");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <p className="text-slate-600">Loading library...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Return to Req Desktop
            </Button>
            <h1 className="text-2xl font-bold text-slate-900">Req Library</h1>
          </div>
          <Button onClick={() => navigate("/builder")}>
            <Plus className="h-4 w-4 mr-2" />
            Create New Requisition
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {libraryReqs.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                No requisitions in library
              </h3>
              <p className="text-slate-600 mb-4">
                Save requisition outputs from the sandbox to build your library
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {libraryReqs.map((req) => (
              <Card key={req.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">
                    {req.name || req.title}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                    {req.description}
                  </p>
                  
                  <div className="flex flex-col gap-2">
                    <Button
                      size="sm"
                      onClick={() => navigate(`/library/${req.id}`)}
                      className="w-full"
                    >
                      View & Edit Input
                    </Button>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCopyToClipboard(req)}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleExportText(req)}
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        Text
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleExportPDF(req)}
                      >
                        <Download className="h-3 w-3 mr-1" />
                        PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleExportWord(req)}
                      >
                        <FileType className="h-3 w-3 mr-1" />
                        Word
                      </Button>
                    </div>

                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(req.id)}
                      className="w-full"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
